import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export const RetroSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select an option...',
  name,
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  const selectedOption = options.find(opt => 
    typeof opt === 'string' ? opt === value : opt.value === value
  );
  
  const displayValue = selectedOption 
    ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.label)
    : placeholder;

  return (
    <div className={`retro-dropdown ${className}`} ref={containerRef}>
      {/* Hidden native select for form submission */}
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      >
        <option value="">{placeholder}</option>
        {options.map((option, idx) => {
          const optValue = typeof option === 'string' ? option : option.value;
          const optLabel = typeof option === 'string' ? option : option.label;
          return (
            <option key={idx} value={optValue}>{optLabel}</option>
          );
        })}
      </select>

      {/* Custom Retro Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        data-open={isOpen}
        className="retro-dropdown-trigger w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <span className={value ? 'text-foreground' : 'text-muted'}>
          {displayValue}
        </span>
        <div className="flex items-center gap-2">
          <div className="pixel-arrow" data-open={isOpen} />
        </div>
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scaleY: 0.8 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -10, scaleY: 0.8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="retro-dropdown-menu origin-top"
          >
            {options.map((option, idx) => {
              const optValue = typeof option === 'string' ? option : option.value;
              const optLabel = typeof option === 'string' ? option : option.label;
              const isSelected = optValue === value;
              
              return (
                <div
                  key={idx}
                  onClick={() => handleSelect(optValue)}
                  data-selected={isSelected}
                  className="retro-dropdown-item"
                >
                  {optLabel}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RetroSelect;
